import React, { useState } from 'react';
import {
  Container,
  Alert,
  Pagination,
  Button,
} from 'react-bootstrap';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import { FaFileExcel } from 'react-icons/fa';
import HeaderAzul from '~/Components/HeaderAzul/HeaderAzul';
import Loading from '~/Components/Loading';
import Tabela from '~/Components/TabelaDinamica/Tabela';
import { gerarRelatorioContas } from '~/Services/Service';
import { filtrosSchema } from './validationSchema';
import FiltrosContas from './components/FiltrosContas';

const ITENS_POR_PAGINA = 20;

const INITIAL_VALUES = {
  codigoInicial: '',
  codigoFinal: '',
  ramoInicial: '',
  ramoFinal: '',
  cidade: '',
  vendedor: '',
  operador: '',
};

const COLUNAS = [
  { title: 'Código', key: 'codigo_conta', ordenacao: true, width: '80px' },
  { title: 'Apelido', key: 'apelido_conta', ordenacao: true },
  { title: 'Tipo', key: 'tipo', ordenacao: true, width: '60px' },
  { title: 'Cód. Ramo', key: 'codigo_ramo', ordenacao: true, width: '90px' },
  { title: 'Status', key: 'status', ordenacao: true, width: '80px' },
  { title: 'GE', key: 'codigo_grupo_empresa', ordenacao: true, width: '60px' },
  { title: 'Limite CR', key: 'limite_cr', ordenacao: true, align: 'right', width: '110px' },
];

const ordenarDataset = (dados, ordenacao) => {
  if (!ordenacao.coluna) return dados;
  return [...dados].sort((a, b) => {
    const valorA = a[ordenacao.coluna] != null ? a[ordenacao.coluna] : '';
    const valorB = b[ordenacao.coluna] != null ? b[ordenacao.coluna] : '';

    if (typeof valorA === 'number' && typeof valorB === 'number') {
      return ordenacao.direcao === 'asc' ? valorA - valorB : valorB - valorA;
    }

    const strA = String(valorA).toLowerCase();
    const strB = String(valorB).toLowerCase();
    if (strA < strB) return ordenacao.direcao === 'asc' ? -1 : 1;
    if (strA > strB) return ordenacao.direcao === 'asc' ? 1 : -1;
    return 0;
  });
};

const RenderizarPaginacao = ({ paginaAtual, totalPaginas, mudarPagina }) => {
  if (totalPaginas <= 1) return null;

  const itens = [];
  const maxBotoes = 5;

  itens.push(
    <Pagination.First key="first" onClick={() => mudarPagina(1)} disabled={paginaAtual === 1} />
  );
  itens.push(
    <Pagination.Prev key="prev" onClick={() => mudarPagina(paginaAtual - 1)} disabled={paginaAtual === 1} />
  );

  let inicioPaginas = Math.max(1, paginaAtual - Math.floor(maxBotoes / 2));
  let fimPaginas = Math.min(totalPaginas, inicioPaginas + maxBotoes - 1);
  if (fimPaginas - inicioPaginas < maxBotoes - 1) {
    inicioPaginas = Math.max(1, fimPaginas - maxBotoes + 1);
  }

  if (inicioPaginas > 1) itens.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);

  for (let numero = inicioPaginas; numero <= fimPaginas; numero += 1) {
    itens.push(
      <Pagination.Item key={numero} active={numero === paginaAtual} onClick={() => mudarPagina(numero)}>
        {numero}
      </Pagination.Item>
    );
  }

  if (fimPaginas < totalPaginas) itens.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);

  itens.push(
    <Pagination.Next key="next" onClick={() => mudarPagina(paginaAtual + 1)} disabled={paginaAtual === totalPaginas} />
  );
  itens.push(
    <Pagination.Last key="last" onClick={() => mudarPagina(totalPaginas)} disabled={paginaAtual === totalPaginas} />
  );

  return (
    <div className="d-flex justify-content-end align-items-center mb-2">
      <Pagination className="mb-0">{itens}</Pagination>
    </div>
  );
};

const RelatorioContasPage = () => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [jaGerou, setJaGerou] = useState(false);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [ordenacao, setOrdenacao] = useState({ coluna: null, direcao: 'asc' });

  const handleGerar = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      const params = {};
      if (values.codigoInicial !== '') params.codigo_ini = values.codigoInicial;
      if (values.codigoFinal !== '') params.codigo_fim = values.codigoFinal;
      if (values.ramoInicial !== '') params.ramo_ini = values.ramoInicial;
      if (values.ramoFinal !== '') params.ramo_fim = values.ramoFinal;
      if (values.cidade) params.cidade_ini = values.cidade;
      if (values.vendedor) params.vendedor_ini = values.vendedor;
      if (values.operador) params.operador_ini = values.operador;

      const res = await gerarRelatorioContas(params);
      const lista = Array.isArray(res) ? res : [];
      setDados(lista);
      setJaGerou(true);
      setPaginaAtual(1);
      setOrdenacao({ coluna: null, direcao: 'asc' });
      if (lista.length === 0) toast.info('Nenhuma conta encontrada com os filtros informados.');
    } catch (err) {
      const mensagem = err && err.message ? err.message : 'Erro ao gerar relatório.';
      toast.error(mensagem);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleLimpar = (resetForm) => {
    resetForm();
    setDados([]);
    setJaGerou(false);
    setPaginaAtual(1);
    setOrdenacao({ coluna: null, direcao: 'asc' });
  };

  const handleExportarExcel = async () => {
    toast.info('Exportação Excel não disponível nesta versão.');
  };

  const handleOrdenacaoChange = (nova) => {
    setOrdenacao(nova);
    setPaginaAtual(1);
  };

  const dadosOrdenados = ordenarDataset(dados, ordenacao);
  const totalRegistros = dadosOrdenados.length;
  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / ITENS_POR_PAGINA));
  const inicioSlice = (paginaAtual - 1) * ITENS_POR_PAGINA;
  const fimSlice = Math.min(inicioSlice + ITENS_POR_PAGINA, totalRegistros);
  const dadosPagina = dadosOrdenados.slice(inicioSlice, inicioSlice + ITENS_POR_PAGINA);

  return (
    <Container fluid className="px-3 py-3">
      <HeaderAzul title="Relatório de Contas" />

      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={filtrosSchema}
        onSubmit={handleGerar}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          resetForm,
        }) => (
          <form onSubmit={handleSubmit}>
            <FiltrosContas
              values={values}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
              onLimpar={() => handleLimpar(resetForm)}
              loading={loading || isSubmitting}
            />

            {loading && <Loading label="Gerando relatório..." />}

            {!loading && dados.length > 0 && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small">
                    Mostrando {inicioSlice + 1} até {fimSlice} de {totalRegistros} registro(s)
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="success"
                    onClick={handleExportarExcel}
                    disabled={loading}
                  >
                    <FaFileExcel className="mr-1" />
                    Exportar Excel
                  </Button>
                </div>

                <RenderizarPaginacao
                  paginaAtual={paginaAtual}
                  totalPaginas={totalPaginas}
                  mudarPagina={setPaginaAtual}
                />

                <Tabela
                  colunas={COLUNAS}
                  dados={dadosPagina}
                  ordenacao={ordenacao}
                  onOrdenacaoChange={handleOrdenacaoChange}
                  striped
                  hover
                />

                <RenderizarPaginacao
                  paginaAtual={paginaAtual}
                  totalPaginas={totalPaginas}
                  mudarPagina={setPaginaAtual}
                />
              </>
            )}

            {!loading && jaGerou && dados.length === 0 && (
              <Alert variant="info" className="py-2">
                Nenhuma conta encontrada com os filtros informados.
              </Alert>
            )}
          </form>
        )}
      </Formik>
    </Container>
  );
};

export default RelatorioContasPage;
